import { CalendarBlankIcon, ToothIcon } from '@phosphor-icons/react'
import moment from 'moment'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'
import { hygieneDaysUntilDue, hygieneDueOn, hygieneMonthsOverdue } from './queueRegistry'

function TimelineStop({ date, caption, state }: { date: string; caption: string; state: `done` | `due` | `late` }) {
  return (
    <div className="flex min-w-0 shrink-0 items-center gap-2">
      <span
        className={cn(
          `flex h-7 w-7 shrink-0 items-center justify-center rounded-full`,
          state === `done` && `bg-surface text-subtle ring-1 ring-inset ring-default`,
          state === `due` && `bg-surface text-default ring-1 ring-inset ring-strong`,
          state === `late` && `bg-red-600 text-on-color`,
        )}
      >
        {state === `done` ? <ToothIcon size={14} weight="fill" /> : <CalendarBlankIcon size={14} weight="bold" />}
      </span>
      <div className="min-w-0">
        <p className={cn(`truncate text-body-sm`, state === `late` ? `text-red-700` : `text-strong`)}>{date}</p>
        <p className={cn(`truncate text-body-xs`, state === `late` ? `text-red-600` : `text-subtle`)}>{caption}</p>
      </div>
    </div>
  )
}

/**
 * The recall as two appointments with time between them: the cleaning that
 * happened, the stretch since, and the one now owed.
 */
export function HygieneTimeline({ contact }: { contact: ContactJson }) {
  const last = contact.lastHygieneDate ? moment.utc(contact.lastHygieneDate) : undefined
  const dueOn = hygieneDueOn(contact)
  if (!last?.isValid() || !dueOn) return null
  const due = moment.utc(dueOn)
  const daysOut = hygieneDaysUntilDue(contact) ?? 0
  const late = daysOut < 0
  const dueToday = daysOut === 0
  const months = hygieneMonthsOverdue(contact)

  // The unit follows the size of the gap. A month count truncates, so anything
  // under 30 days late rendered as "0 months overdue"; below three months the
  // honest unit is days, and past two years it is years.
  const daysLate = Math.max(0, -daysOut)
  const gapLabel = late
    ? daysLate < 90
      ? `${daysLate} ${daysLate === 1 ? `day` : `days`} overdue`
      : months != null && months >= 24
        ? `${Math.floor(months / 12)} years overdue`
        : `${months} ${months === 1 ? `month` : `months`} overdue`
    : dueToday
      ? `due today`
      : `in ${daysOut} ${daysOut === 1 ? `day` : `days`}`

  return (
    <div className="mt-4 flex items-center gap-3">
      <TimelineStop date={last.format(`MMM D, YYYY`)} caption="Last cleaning" state="done" />

      {/* Stretches to the row's full height and centres its own content, so
          the line lands on the icons' centreline; the label is positioned off
          that line so it cannot push it off-centre. */}
      <div className="relative flex min-w-0 flex-1 items-center self-stretch">
        <span
          className={cn(
            `w-full border-t border-dashed`,
            late ? `border-red-400` : dueToday ? `border-red-300` : `border-strong`,
          )}
        />
        <span
          className={cn(
            `absolute inset-x-0 top-1/2 mt-1.5 truncate text-center text-body-xs`,
            late || dueToday ? `font-medium text-red-600` : `text-subtle`,
          )}
        >
          {gapLabel}
        </span>
      </div>

      <TimelineStop
        date={due.format(`MMM D, YYYY`)}
        caption={late ? `Was due` : `Next cleaning`}
        state={late || dueToday ? `late` : `due`}
      />
    </div>
  )
}
